import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Grid
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Subscription, ISubscriptionType } from '../../../resources/types/balanceTypes';
import { selectSubscriptions } from '../../../redux/slices/subscriptionSlice';
import axios from '../../../resources/axios';
import { selectAdmins } from '../../../redux/slices/adminsSlice';

interface SubscriptionPlansTabProps {
  onEdit: (subscription: Subscription) => void;
  onDelete: (id: string) => void;
}

interface UserSubscription {
  _id: string;
  subscriptionPlan: {
    _id: string;
    name: string;
  };
  city: {
    _id: string;
  };
}

const SubscriptionPlansTab: React.FC<SubscriptionPlansTabProps> = ({ onEdit, onDelete }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { subscriptions } = useSelector(selectSubscriptions);
  const adminInfo = useSelector(selectAdmins);
  const [soldCounts, setSoldCounts] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    fetchSoldCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminInfo.currentCity]);

  const fetchSoldCounts = async () => {
    try {
      const response = await axios.get<{ data: { subscriptions: UserSubscription[] } }>(
        '/subscription/admin/user_subscriptions',
        {
          params: {
            limit: 1000,
            page: 1
          }
        }
      );
      
      console.log('Sold counts API response:', response.data);
      
      const allSubscriptions = response.data.data.subscriptions || [];
      console.log('All subscriptions for counting:', allSubscriptions);
      
      const cityFiltered = allSubscriptions.filter(
        (sub: UserSubscription) => sub.city._id === adminInfo.currentCity
      );
      console.log('City filtered subscriptions:', cityFiltered);

      const counts: { [key: string]: number } = {};
      cityFiltered.forEach((sub: UserSubscription) => {
        const planId = sub.subscriptionPlan._id;
        counts[planId] = (counts[planId] || 0) + 1;
      });

      console.log('Final sold counts:', counts);
      setSoldCounts(counts);
    } catch (error) {
      console.error('Error fetching sold counts:', error);
      setSoldCounts({});
    }
  };

  const getFeatures = (subscription: Subscription): string[] => {
    if (subscription.description && subscription.description.trim()) {
      return subscription.description.split('\n').filter(line => line.trim());
    }
    
    return [t('subscriptions.descriptionUnavailable')];
  };

  const getStatusBadge = (subscription: Subscription) => {
    if (subscription.type === ISubscriptionType.freeContractActions) {
      return null;
    }
    if (subscription.type === ISubscriptionType.discountPercentForContractActions) {
      return (
        <Chip 
          label={t('subscriptions.inactive')} 
          size="small" 
          sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: theme.palette.mode === 'light' ? '#FFF3E0' : 'rgba(255, 152, 0, 0.2)',
            color: '#FF9800',
            fontWeight: 600
          }} 
        />
      );
    }
    return null;
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {subscriptions?.items?.map(subscription => {
          const features = getFeatures(subscription);
          const soldCount = soldCounts[subscription._id || ''] || 0;
          
          return (
            <Grid item xs={12} sm={6} md={4} key={subscription._id}>
              <Card
                sx={{
                  position: 'relative',
                  height: '100%',
                  borderRadius: 2,
                  border: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                  backgroundColor: theme.palette.mode === 'light' ? '#FAFAFA' : '#2C2C2C',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'light' 
                      ? '0 8px 24px rgba(0,0,0,0.12)' 
                      : '0 8px 24px rgba(0,0,0,0.4)'
                  }
                }}
              >
                {getStatusBadge(subscription)}
                
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      mb: 2,
                      color: theme.palette.mode === 'light' ? '#212121' : '#FFF'
                    }}
                  >
                    {subscription.name}
                  </Typography>

                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      mb: 0.5,
                      color: theme.palette.mode === 'light' ? '#212121' : '#FFF'
                    }}
                  >
                    {t('subscriptions.pricePerMonth', { price: subscription.price })}
                  </Typography>
                  
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: theme.palette.mode === 'light' ? '#9E9E9E' : '#808080',
                      mb: 2
                    }}
                  >
                    {t('subscriptions.duration', { duration: subscription.duration })}
                  </Typography>

                  <Box sx={{ flex: 1 }}>
                    {features.map((feature, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start',
                          gap: 1,
                          mb: 1
                        }}
                      >
                        <CheckCircleIcon 
                          sx={{ 
                            fontSize: 18,
                            color: theme.palette.mode === 'light' ? '#4CAF50' : '#66BB6A',
                            mt: 0.2
                          }} 
                        />
                        <Typography 
                          variant="body2"
                          sx={{ 
                            color: theme.palette.mode === 'light' ? '#424242' : '#E0E0E0'
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#424242'}`
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: theme.palette.mode === 'light' ? '#9E9E9E' : '#808080'
                      }}
                    >
                      {t('subscriptions.sold', { count: soldCount })}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('subscriptions.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(subscription)}
                          sx={{
                            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#3A3A3A',
                            color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            borderRadius: '6px',
                            '&:hover': { 
                              bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#4A4A4A' 
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('subscriptions.delete')}>
                        <IconButton
                          size="small"
                          onClick={() => subscription._id && onDelete(subscription._id)}
                          sx={{
                            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#3A3A3A',
                            color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            borderRadius: '6px',
                            '&:hover': { 
                              bgcolor: theme.palette.mode === 'light' ? '#FFEBEE' : '#4A3A3A',
                              color: '#F44336'
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {!subscriptions?.items?.length && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Typography variant="body1">
            {t('subscriptions.noPlans')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionPlansTab;
